import { Card } from 'app/types/cards/Card';
import { CardTypeEnum } from 'app/types/enums/CardTypeEnum';
import { createUrl, formatDate, formatSecondsToTime } from 'app/utils';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';
import { AnalysisReportCard, CourseCard, EventCard, ExamCard, LiveCard, MasterclassCard, ProgramCard, RecommendationCard } from 'components';
import { CardTags } from 'components/cards/course';

import { IconsTypes } from 'components/icons';

import { DashboardAreaColorEnum, DashboardVisualizationTypeEnum } from 'features/dashboard-new';
import { StudentPracticeExamAssessmentStatusEnum } from 'features/exams';
import { useLives } from 'features/lives';

import { ProductTypeEnum } from 'features/products';
import { AnalysisReportStatusEnum } from 'features/reports';

interface CardProps {
  card: Card;
  dashboardAreaColor: DashboardAreaColorEnum;
  dashboardVisualizationType: DashboardVisualizationTypeEnum;
  productEnrolled?: boolean;
  journeyProductSlug?: string;
}

const ProductCard = ({ card, dashboardAreaColor, dashboardVisualizationType, productEnrolled = true, journeyProductSlug }: CardProps) => {
  const theme =
    dashboardAreaColor === DashboardAreaColorEnum.Dark
      ? 'dark'
      : dashboardVisualizationType === DashboardVisualizationTypeEnum.Cinema
      ? 'dark'
      : 'light';

  const { sanitizeCardToLiveCard } = useLives();

  switch (card.cardType) {
    case CardTypeEnum.Course:
      const course = card.course;

      if (!course) return <></>;

      let cardLink = '';

      if (course.productType === ProductTypeEnum.Program) {
        cardLink = createUrl('programDetails', [course.slug]);
      } else {
        cardLink = createUrl(course.productClassUuid ? 'productClassDetails' : 'productDetails', [course.slug, course.productClassUuid]);
      }

      let coursesTags: CardTags = [];

      let courseDisabled = false;
      if (!course.detailedAccess) {
        courseDisabled = true;
        coursesTags.push('soon');
      }

      return (
        <>
          <CourseCard
            link={cardLink}
            hat={course.hat}
            name={course.name}
            image={`${course.cardImageUrl}`}
            evolution={course.completionPercentage || undefined}
            enrolled={course.enrolled}
            theme={theme}
            disabled={courseDisabled}
            tags={coursesTags}
          />
          {journeyProductSlug}
        </>
      );

    case CardTypeEnum.AnalysisReport:
      const analysisReport = card.analysisReport;

      if (!analysisReport) return <></>;

      let reportButtonIcon: IconsTypes = undefined!;
      let reportButtonLabel = '';
      let analysisReportStatus = analysisReport.analysisReportStatus;

      switch (analysisReportStatus) {
        case AnalysisReportStatusEnum.Scheduled:
          reportButtonIcon = 'calendarClock';
          reportButtonLabel = `DISPONÍVEL ${formatDate(analysisReport.availableAt).format('DD/MM/YYYY HH[h]mm')}`;
          break;

        case AnalysisReportStatusEnum.Available:
          reportButtonIcon = 'trayDownload';
          reportButtonLabel = `BAIXAR`;

          if (analysisReport.downloadedAt) {
            reportButtonIcon = 'progressDownload';
            reportButtonLabel = `BAIXADO EM ${formatDate(analysisReport.downloadedAt).format('DD/MM/YYYY')}`;
          }

          break;

        case AnalysisReportStatusEnum.Unavailable:
          reportButtonIcon = 'lockOutline';
          reportButtonLabel = `INDISPONÍVEL`;

          if (!analysisReport.allowedCourses?.length) {
            reportButtonIcon = 'timerSandComplete';
            analysisReportStatus = AnalysisReportStatusEnum.Expired;
          }
          break;

        case AnalysisReportStatusEnum.Expired:
          reportButtonIcon = 'timerSandComplete';
          reportButtonLabel = `EXPIRADO`;
          break;

        default:
          break;
      }

      return (
        <AnalysisReportCard
          typeName={analysisReport.analysisReportTypeName}
          date={analysisReport.writtenAt}
          providerName={analysisReport.analysisReportProviderName}
          name={analysisReport.name}
          status={analysisReportStatus}
          uuid={card.uuid}
          downloadFilename={analysisReport.downloadFilename}
          buttonIcon={reportButtonIcon}
          buttonLabel={reportButtonLabel}
          allowedCourses={analysisReport.allowedCourses}
          theme={theme}
        />
      );

    case CardTypeEnum.Recommendation:
      const recommendation = card.recommendation;

      if (!recommendation) return <></>;

      return (
        <RecommendationCard
          companyName={recommendation.companyName}
          symbolCode={recommendation.symbolCode}
          highlighted={recommendation.highlighted}
          updatedAt={recommendation.updatedAt}
          title={recommendation.title}
          link={createUrl('recommendations/details', [recommendation.productSlug, recommendation.productClassUuid, card.uuid])}
        />
      );

    case CardTypeEnum.LiveVideo:
      const liveVideo = sanitizeCardToLiveCard(card?.liveVideo!, { journeyProductSlug });
      if (!liveVideo) return <></>;

      return <LiveCard {...liveVideo} theme={theme} disabled={liveVideo.disabled} productEnrolled={productEnrolled} />;

    case CardTypeEnum.PracticeExam:
      const practiceExam = card.practiceExam;

      if (!practiceExam) return <></>;

      const examName = practiceExam.name;
      const questionsCount = `${practiceExam.questionCount} QUESTÕES`;
      let examLink: string | undefined = createUrl('exams/start', [card.uuid]);
      let examDuration = practiceExam.durationSeconds ? formatSecondsToTime(practiceExam.durationSeconds) : '';
      let examDisabled = false;
      let examButtonLabel = '';
      let examButtonIcon: IconsTypes = 'arrowRight';

      let releaseDate = '';
      if (practiceExam.releaseDate) {
        releaseDate = formatDateFromUtcToTimezone(practiceExam.releaseDate).format('DD/MM HH[h]mm');
      }

      switch (practiceExam.assessmentStatus) {
        case StudentPracticeExamAssessmentStatusEnum.AvailableToBuy:
          examDisabled = true;
          examButtonLabel = 'ADQUIRIR';
          break;

        case StudentPracticeExamAssessmentStatusEnum.ToBeReleased:
          examButtonIcon = 'calendar';
          examButtonLabel = releaseDate || 'INDISPONIVEL';
          examLink = undefined;
          examDisabled = true;
          break;

        case StudentPracticeExamAssessmentStatusEnum.ReleasedNotStarted:
          examButtonLabel = 'INICIAR';
          break;

        case StudentPracticeExamAssessmentStatusEnum.StartedNotFinished:
          examButtonLabel = 'CONTINUAR';
          break;

        case StudentPracticeExamAssessmentStatusEnum.Finished:
          if (practiceExam.studentPracticeExamAssessment) {
            examButtonLabel = 'VER RESULTADO';

            if (practiceExam.allowStartPracticeExamCount) {
              examButtonIcon = 'refresh';
              examButtonLabel = 'REFAZER';
            }

            examLink = createUrl('exams/result', [card.uuid, practiceExam.studentPracticeExamAssessment]);
          }
          break;

        case StudentPracticeExamAssessmentStatusEnum.NotDone:
          examButtonLabel = 'NÃO REALIZADA';
          examLink = undefined;
          examDisabled = true;
          break;

        case StudentPracticeExamAssessmentStatusEnum.Unavailabled:
          examButtonIcon = 'lockOutline';
          examButtonLabel = 'INDISPONÍVEL';
          // showOverlay = !!practiceExam.allowedCourses.length
          examLink = undefined;
          examDisabled = true;
          break;

        case StudentPracticeExamAssessmentStatusEnum.Blocked:
          examButtonIcon = 'lockOutline';
          examButtonLabel = 'BLOQUEADO';
          examLink = undefined;
          examDisabled = true;
          break;

        case StudentPracticeExamAssessmentStatusEnum.Unspecified:
        default:
          break;
      }

      return (
        <ExamCard
          name={examName}
          questionsCount={questionsCount}
          link={examLink}
          duration={examDuration}
          buttonLabel={examButtonLabel}
          buttonIcon={examButtonIcon}
          disabled={examDisabled}
          allowedCourses={practiceExam.allowedCourses}
          theme={theme}
        />
      );

    case CardTypeEnum.Program:
      const program = card.program;

      if (!program) return <></>;

      const programCardLink = createUrl('programDetails', [program.slug]);

      let programTags: CardTags = [];

      let programDisabled = false;
      if (!program.detailedAccess) {
        programDisabled = true;
        programTags.push('soon');
      }

      if (program.isNew) {
        programTags.push('new');
      }

      return (
        <ProgramCard
          link={programCardLink}
          hat={program.hat}
          name={program.name}
          image={`${program.cardImageUrl}`}
          enrolled={program.enrolled}
          theme={theme}
          disabled={programDisabled}
          tags={programTags}
        />

        // <CourseCard
        //   link={programCardLink}
        //   hat={program.hat}
        //   name={program.name}
        //   image={`${program.cardImageUrl}`}
        //   enrolled={program.enrolled}
        //   theme={theme}
        // />
      );

    case CardTypeEnum.Journey:
      const journey = card.journey;

      if (!journey) return <></>;

      const journeyCardLink = createUrl('journeys/details', [journey.slug]);

      let journeyTags: CardTags = [];

      let journeyDisabled = false;
      if (!journey.detailedAccess) {
        journeyDisabled = true;
        journeyTags.push('soon');
      }

      if (journey.isNew) {
        journeyTags.push('new');
      }

      return (
        <ProgramCard
          link={journeyCardLink}
          hat={journey.hat}
          name={journey.name}
          image={`${journey.cardImageUrl}`}
          enrolled={journey.enrolled}
          theme={theme}
          disabled={journeyDisabled}
          tags={journeyTags}
        />
      );

    case CardTypeEnum.MasterClass:
      const masterClass = card.masterClass;

      if (!masterClass) return <></>;

      const masterClassCardLink = createUrl('masterClassDetails', [masterClass.slug, masterClass.productClassUuid]);

      let masterClassTags: CardTags = [];

      let masterClassDisabled = false;
      if (!masterClass.detailedAccess) {
        masterClassDisabled = true;
        masterClassTags.push('soon');
      }

      if (masterClass.isNew) {
        masterClassTags.push('new');
      }

      return (
        <MasterclassCard
          link={masterClassCardLink}
          hat={masterClass.hat}
          name={masterClass.name}
          image={`${masterClass.cardImageUrl}`}
          enrolled={masterClass.enrolled}
          theme={theme}
          disabled={masterClassDisabled}
          tags={masterClassTags}
        />
      );
    case CardTypeEnum.Event:
      const event = card.event;

      if (!event) return <></>;

      let eventTags: CardTags = [];

      let eventDisabled = false;
      if (!event.detailedAccess) {
        eventDisabled = true;
        eventTags.push('soon');
      }

      if (event.isNew) {
        eventTags.push('new');
      }

      return (
        <EventCard
          uuid={card.uuid || ''}
          name={event?.name}
          hat={event?.hat}
          slug={event?.slug}
          hatName={event?.hatName}
          startDate={event?.startDate}
          checkinStartDate={event?.checkinStartDate}
          enrolled={event.enrolled}
          allowEnrollemnts={event.allowEnrollemnts}
          buttonLabel="Acessar"
          buttonIcon="calendar"
          image={event?.cardImageUrl}
          commentContentUuid={event?.commentContentUuid}
          size="default"
          hideButton={false}
          disabled={eventDisabled}
        />
      );
    default:
      return <></>;
  }
};

export default ProductCard;
